import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { AudioProvider, useAudio } from "../hooks/useAudio";
import { useAuth } from "../hooks/useAuth";
import { useFullScreen } from "../hooks/useFullScreen";
import { useInteraction } from "../hooks/useInteraction";
import { useLogs } from "../hooks/useLogs";
import { useMusicControl } from "../hooks/useMusicControl";
import { ScreenInfoProvider, useScreenInfo } from "../hooks/useScreen";
import { CustomScreen } from "../hooks/useScreen/types";
import {
  ScreenSocketInfoProvider,
  useScreenSocketInfo,
} from "../hooks/useScreenSocket";
import { cn } from "../lib/utils";
import { isInsideDashboard } from "../utils/isInsideDashboard";
import isInsideIframe from "../utils/isInsideIframe";
import ExitPopupDialog from "./ExitPopupDialog";
import LoadingContainer from "./LoadingConitainer";
import { SignageContentPlayer } from "./Players";
import TVFrame from "./TVFrame";
import { MusicController } from "./ui/MusicController";

type PresenterProps = {
  mountExitPopup?: boolean;
}
type Props = {
  initialScreenInfo: CustomScreen | null;
} & PresenterProps;
const OrganizationScreen = (props: Props) => {
  return (
    <ScreenInfoProvider {...props}>
      <OrganizationScreenPresenter {...props} />
    </ScreenInfoProvider>
  );
};

function OrganizationScreenPresenter(props: PresenterProps) {
  const handle = useFullScreen();
  const { screenInfo } = useAuth()

  const [showFullScreenButton, setShowFullScreenButton] = useState(false);
  const timerRef = useRef<number | null>(null);

  // Helper function to hide the button after 3 seconds
  const hideButton = useCallback(() => {
    setShowFullScreenButton(false);
  }, []);

  // Track when mouse enters and leaves
  const handleMouseEnter = () => {
    setShowFullScreenButton(true);
    // Clear any existing timeout to prevent it from hiding the button prematurely
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const handleMouseLeave = () => {
    // Set a new timeout to hide the button after 3 seconds
    timerRef.current = window.setTimeout(hideButton, 3000);
  };

  useEffect(() => {
    return () => {
      // Clear any ongoing timeout when the component unmounts
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const { mutateAsync: removeMusicListener } = useMusicListener.removeListener()

  const audioUrl = screenInfo?.group_id ? screenInfo.group_music_infos?.[0]?.url : screenInfo?.music_infos?.[0]?.url ?? ""

  return (
    <div
      className="h-screen w-screen bg-white"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {handle.shouldShowFullScreenButton && (
        <>
          <button
            onClick={handle.enter}
            className={`text absolute right-8 top-8 z-10 rounded-md bg-slate-800 p-2 text-white transition-all ${!showFullScreenButton ? "opacity-0" : ""
              }`}
          >
            <FullScreenButton />
          </button>
        </>
      )}
      <div
        aria-hidden="true"
        className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-white text-black"
      >
        {screenInfo && (
          <AudioProvider
            url={audioUrl}
            onError={async () => {
              if (screenInfo.group_id) await removeMusicListener({ group_id: screenInfo.group_id })
              else await removeMusicListener({ zone_id: screenInfo!.zone_id! })

              await new Promise((resolve) => {
                setTimeout(resolve, 5000)
              })
            }}>
            <Player
              key={screenInfo.id}
              showFullScreenButton={showFullScreenButton}
              {...props}
            />
          </AudioProvider>
        )}
      </div>
    </div>
  );
}

function Player({ showFullScreenButton = true, mountExitPopup = false }: { showFullScreenButton: boolean } & PresenterProps) {
  const { screenInfo } = useAuth()
  const { onLayoutChanged, onScreenChanged, onPlaylistChanged } = useScreenInfo();
  const { onTrackChange, onPause, onPlay, onEnable, onDisable, musicEnabled, onSyncDisabled, onSyncEnabled } = useAudio()
  const { controlInfo } = useMusicControl(screenInfo?.id, screenInfo?.location_id, screenInfo?.group_id)
  const hasControl = !screenInfo?.group_id ? true : (controlInfo?.is_owner || controlInfo?.has_control)

  return <ScreenSocketInfoProvider
    onMusicPaused={onPause}
    onMusicResumed={onPlay}
    onMusicDisabled={onDisable}
    onMusicEnabled={onEnable}
    onMusicChanged={onTrackChange}
    onLayoutChanged={onLayoutChanged}
    onScreenChanged={onScreenChanged}
    onPlaylistChanged={onPlaylistChanged}
    onMusicSyncDisabled={onSyncDisabled}
    onMusicSyncEnabled={onSyncEnabled}
  >
    <ExitPopupDialog
      musicEnabled={musicEnabled && hasControl}
      hasError={false}
      isLoading={false}
      isPaired
      mountIfError={false}
      shouldShowRequestSyncDialog={!!screenInfo?.group_id && !hasControl}
      mounted={mountExitPopup}>
      {
        !isInsideDashboard() &&
        <MusicPlayer showFullScreenButton={showFullScreenButton} />
      }
      <OrganizationScreenPlayer screenInfo={screenInfo} />
    </ExitPopupDialog>
  </ScreenSocketInfoProvider>
}

function MusicPlayer({ showFullScreenButton }: { showFullScreenButton: boolean }) {
  const { screenInfo } = useAuth()
  const { controlInfo } = useMusicControl(screenInfo?.id, screenInfo?.location_id, screenInfo?.group_id)
  const hasMusicControl = !screenInfo?.group_id ? true : (controlInfo?.is_owner || controlInfo?.has_control)

  const { onPlay, onPause, onVolumeChange, musicEnabled } = useAudio()

  if (!musicEnabled) return null;

  if (!screenInfo?.zone_id) return null;
  const track = screenInfo.music_infos?.[0] ?? null
  if (!track) return null

  return <MusicController
    className={cn("absolute top-8 z-10 left-1/2", {
      "opacity-0": !showFullScreenButton || navigator.userAgent.includes("TV") || isInsideIframe(),
    })}
    onPlay={onPlay}
    onPause={onPause}
    onVolumeChange={onVolumeChange}
    hidden={!hasMusicControl}
  />
}

function OrganizationScreenPlayer({
  screenInfo,
}: {
  screenInfo?: CustomScreen | null;
}) {
  const { isOnline } = useScreenSocketInfo();
  const { showLogs, log } = useLogs();

  const { hasInteracted } = useInteraction()
  const { onVolumeChange } = useAudio()

  useEffect(() => {
    // @ts-expect-error
    log({ eventtype: "Journey", name: "OrgScreen", payload: { hasInteracted } })
  }, [hasInteracted])

  return (
    <div className="h-full w-full">
      {showLogs && (
        <div className="absolute left-2 top-2 z-10 flex items-center justify-center gap-2 rounded-full border bg-white px-4 py-1 text-sm">
          <div
            className={`h-3 w-3 rounded-full ${isOnline ? "bg-green-500" : "bg-red-500"
              }`}
          ></div>
          {isOnline ? "Online" : "Offline"}
        </div>
      )}
      <TVFrameWrapper>
        <div className="w-full h-full" id="renderRoot">
          <SignageContentPlayer
            {...{
              screenInfo,
              hasInteracted,
              loading: <LoadingContainer />,
              setMusicVolume: onVolumeChange,
            }}
          />
        </div>
      </TVFrameWrapper>
    </div>
  );
}

function TVFrameWrapper({ children }: { children: React.ReactNode }) {
  const searchParams = new URLSearchParams(window.location.search);
  const showTvFrame = searchParams.get("frame") ?? true

  if (!showTvFrame) return children

  return <TVFrame>{children}</TVFrame>
}

const FullScreenButton = () => {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 3H9V5H6.46173L11.3047 9.84298L9.8905 11.2572L5 6.3667V9H3V3Z"
        fill="currentColor"
      />
      <path
        d="M3 21H9V19H6.3764L11.3046 14.0718L9.89038 12.6576L5 17.548V15H3V21Z"
        fill="currentColor"
      />
      <path
        d="M15 21H21V15H19V17.5244L14.1332 12.6576L12.719 14.0718L17.6472 19H15V21Z"
        fill="currentColor"
      />
      <path
        d="M21 3H15V5H17.5619L12.7189 9.84301L14.1331 11.2572L19 6.39032V9H21V3Z"
        fill="currentColor"
      />
    </svg>
  );
};

export type ListenerGroup = {
  id: number,
  name: string,
  locations: number[],
  url: string
}

export const useMusicListener = {
  $metadata: {
    headers() {
      const headers = new Headers();
      headers.append("Authorization", `Bearer ${import.meta.env.VITE_MUSIC_API_KEY}`);
      return headers
    },
    fetchers: {
      async getAll() {
        const headers = useMusicListener.$metadata.headers()
        const apiUrl = `${import.meta.env.VITE_MUSIC_API_URL}/api/partner/listeners`

        const resp = await fetch(apiUrl, {
          method: "POST",
          headers,
        })

        const musicInfo = await resp.json()
        if (musicInfo.status === "success") {
          return musicInfo.listeners
        }
        throw new Error(musicInfo.message)
      },
      async getWithZone(zone_id: number) {
        const listeners = await useMusicListener.$metadata.fetchers.getAll()
        return listeners.find((e: any) => e.zone_id === zone_id)
      },
      async getWithGroup(group_id: number) {
        const listeners = await useMusicListener.$metadata.fetchers.getAll()
        return listeners.find((e: any) => e.group_id === group_id)
      },
    },
  },
  removeListener() {
    const headers = this.$metadata.headers()
    const getWithZone = this.$metadata.fetchers.getWithZone
    const getWithGroup = this.$metadata.fetchers.getWithGroup
    return useMutation({
      async mutationFn({ zone_id, group_id }: { zone_id?: number, group_id?: number }) {
        const listener = zone_id ? await getWithZone(zone_id) : await getWithGroup(group_id!)
        if (!listener) return

        const apiUrl = `${import.meta.env.VITE_MUSIC_API_URL}/api/partner/listener/remove`

        const resp = await fetch(apiUrl, {
          method: "POST",
          headers,
          body: JSON.stringify({
            id: listener.id,
          })
        })
        const json = await resp.json()
        if (json.status === "success") {
          return json
        }
        if (json.message.toString().includes("not found")) return
        throw new Error(json.message)
      }
    })
  }
}
export default OrganizationScreen;
